// src/components/DashboardSidebar.jsx

import {
  Badge,
  Box,
  Divider,
  Flex,
  Link as ChakraLink,
  Text,
  VStack,
} from "@chakra-ui/react";
import { Link, useLocation } from "react-router-dom";
import useBusinessGuard from "../hooks/useBusinessGuard";

const BRAND_RED = "#E63946";
const BRAND_RED_DARK = "#C92A37";
const BRAND_RED_LIGHT = "#FFF5F5";
const BRAND_BORDER = "#FED7D7";

const PAID_PLANS = [
  "pro",
  "growth",
  "agency",
];

export default function DashboardSidebar({
  flyerId,
  groupId,
  slotCount = 0,
  dispatchQueueLength = 0,
  notifications = 0,
  activeRegion = null,
}) {
  const location = useLocation();

  const {
    profile,
    loading,
  } = useBusinessGuard();

  const userRole = String(
    profile?.user_role || "business"
  ).toLowerCase();

  const currentPlan = String(
    profile?.plan || "free"
  ).toLowerCase();

  const hasProAccess =
    PAID_PLANS.includes(currentPlan);

  const canSeeAdmin =
    userRole === "admin" ||
    userRole === "developer";

  const isActive = (path) => {
    if (path === "/dashboard") {
      return location.pathname === "/dashboard";
    }

    return location.pathname.startsWith(path);
  };

  const NavItem = ({
    to,
    label,
    badge = null,
  }) => {
    const active = isActive(to);

    const showBadge =
      badge !== null &&
      badge !== undefined &&
      badge !== "" &&
      badge !== 0;

    return (
      <Flex
        width="100%"
        minHeight="44px"
        align="center"
        justify="space-between"
        px={3}
        py={2}
        borderRadius="lg"
        borderWidth="1px"
        borderColor={
          active
            ? BRAND_BORDER
            : "transparent"
        }
        bg={
          active
            ? BRAND_RED_LIGHT
            : "transparent"
        }
        color={
          active
            ? BRAND_RED_DARK
            : "gray.700"
        }
        fontWeight={
          active
            ? "bold"
            : "medium"
        }
        transition="all 0.15s ease"
        _hover={{
          bg: BRAND_RED_LIGHT,
          color: BRAND_RED_DARK,
          borderColor: BRAND_BORDER,
        }}
      >
        <ChakraLink
          as={Link}
          to={to}
          flex="1"
          minWidth={0}
          display="block"
          fontSize="sm"
          textDecoration="none"
          _hover={{
            textDecoration: "none",
          }}
          _focusVisible={{
            outline: "2px solid",
            outlineColor: BRAND_RED,
            outlineOffset: "2px",
            borderRadius: "md",
          }}
        >
          <Text noOfLines={1}>
            {label}
          </Text>
        </ChakraLink>

        {showBadge ? (
          <Badge
            ml={2}
            flexShrink={0}
            px={2}
            py={0.5}
            borderRadius="full"
            borderWidth="1px"
            borderColor={
              active
                ? BRAND_RED
                : BRAND_BORDER
            }
            bg={
              active
                ? BRAND_RED
                : BRAND_RED_LIGHT
            }
            color={
              active
                ? "white"
                : BRAND_RED_DARK
            }
            fontSize="10px"
            fontWeight="bold"
          >
            {badge}
          </Badge>
        ) : null}
      </Flex>
    );
  };

  if (loading && !profile) {
    return (
      <Box
        width="100%"
        p={5}
      >
        <Text
          color="gray.500"
          fontSize="sm"
          textAlign="center"
        >
          Loading menu…
        </Text>
      </Box>
    );
  }

  return (
    <Box
      width="100%"
      minWidth={0}
      bg="white"
      px={{
        base: 4,
        md: 2,
      }}
      py={{
        base: 4,
        md: 2,
      }}
      overflowX="hidden"
      overflowY="auto"
    >
      {/* BRAND */}

      <Box
        px={3}
        mb={5}
        textAlign="center"
      >
        <Text
          color={BRAND_RED}
          fontSize="lg"
          fontWeight="extrabold"
        >
          Postcode.Coupons
        </Text>

        <Text
          mt={1}
          color="gray.500"
          fontSize="xs"
        >
          Campaign Cockpit
        </Text>

        {hasProAccess ? (
          <Badge
            mt={3}
            px={3}
            py={1}
            borderRadius="full"
            bg={BRAND_RED}
            color="white"
            fontSize="10px"
            fontWeight="bold"
          >
            PRO PLAN
          </Badge>
        ) : null}
      </Box>

      <Divider
        mb={5}
        borderColor={BRAND_BORDER}
      />

      <VStack
        align="stretch"
        spacing={5}
        width="100%"
      >
        {/* MAIN */}

        <Box>
          <Text
            px={3}
            mb={2}
            color="gray.500"
            fontSize="xs"
            fontWeight="bold"
            letterSpacing="wide"
          >
            MAIN
          </Text>

          <VStack
            align="stretch"
            spacing={1}
          >
            <NavItem
              to="/dashboard"
              label="🏠 Dashboard"
            />

            <NavItem
              to="/account"
              label="👨‍💼 My Account"
            />

            <NavItem
              to="/my-flyers"
              label="📰 My Flyers"
            />

            <NavItem
              to="/my-bookings"
              label="📅 My Bookings"
            />

            <NavItem
              to="/invoices"
              label="🧾 Invoices"
            />

            <NavItem
              to="/credit-history"
              label="💳 Credit History"
            />

            <NavItem
              to="/dashboard/qr-analytics"
              label="📡 QR Analytics"
              badge={
                hasProAccess
                  ? null
                  : "PRO"
              }
            />
          </VStack>
        </Box>

        {/* FLYER TOOLS */}

        {flyerId ? (
          <Box>
            <Divider
              mb={5}
              borderColor="gray.200"
            />

            <Text
              px={3}
              mb={2}
              color="gray.500"
              fontSize="xs"
              fontWeight="bold"
              letterSpacing="wide"
            >
              FLYER TOOLS
            </Text>

            <VStack
              align="stretch"
              spacing={1}
            >
              <NavItem
                to={`/flyer/${encodeURIComponent(
                  flyerId
                )}/preview`}
                label="🗺️ Slot Map Preview"
                badge={
                  slotCount > 0
                    ? slotCount
                    : null
                }
              />

              <NavItem
                to={`/flyer/${encodeURIComponent(
                  flyerId
                )}/audit`}
                label="🔎 Slot Audit"
                badge="Audit"
              />
            </VStack>
          </Box>
        ) : null}

        {/* GROUP TOOLS */}

        {groupId ? (
          <Box>
            <Divider
              mb={5}
              borderColor="gray.200"
            />

            <Text
              px={3}
              mb={2}
              color="gray.500"
              fontSize="xs"
              fontWeight="bold"
              letterSpacing="wide"
            >
              GROUP TOOLS
            </Text>

            <VStack
              align="stretch"
              spacing={1}
            >
              <NavItem
                to={`/group/${encodeURIComponent(
                  groupId
                )}/bundle-export`}
                label="📚 PDF Bundle Export"
                badge={`Group ${groupId}`}
              />
            </VStack>
          </Box>
        ) : null}

        {/* DISCOVERY */}

        <Box>
          <Divider
            mb={5}
            borderColor="gray.200"
          />

          <Text
            px={3}
            mb={2}
            color="gray.500"
            fontSize="xs"
            fontWeight="bold"
            letterSpacing="wide"
          >
            DISCOVERY
          </Text>

          <VStack
            align="stretch"
            spacing={1}
          >
            <NavItem
              to="/redeem"
              label="🎫 Redeem Page"
            />

            <NavItem
              to={
                activeRegion
                  ? `/coupons/${encodeURIComponent(
                      activeRegion
                    )}`
                  : "/coupons"
              }
              label="🏷️ Coupons by Postcode"
              badge={
                activeRegion ||
                null
              }
            />
          </VStack>
        </Box>

        {/* ADMIN ROLE GUARD */}

        {canSeeAdmin ? (
          <Box>
            <Divider
              mb={5}
              borderColor="gray.200"
            />

            <Text
              px={3}
              mb={2}
              color="gray.500"
              fontSize="xs"
              fontWeight="bold"
              letterSpacing="wide"
            >
              ADMIN
            </Text>

            <VStack
              align="stretch"
              spacing={1}
            >
              <NavItem
                to="/referrals"
                label="🚀 Referral Dashboard"
              />

              <NavItem
                to="/admin/cockpit"
                label="⚙️ Admin Cockpit"
                badge={
                  dispatchQueueLength > 0
                    ? dispatchQueueLength
                    : null
                }
              />

              <NavItem
                to="/debug"
                label="🧪 Debug Tools"
                badge={
                  notifications > 0
                    ? notifications
                    : null
                }
              />
            </VStack>
          </Box>
        ) : null}
      </VStack>

      {/* FOOTER */}

      <Divider
        mt={6}
        borderColor={BRAND_BORDER}
      />

      <Box
        mt={4}
        px={3}
        textAlign="center"
      >
        <Text
          color="gray.400"
          fontSize="xs"
        >
          Postcode.Coupons Cockpit v1.1
        </Text>

        <Text
          mt={1}
          color="gray.400"
          fontSize="10px"
        >
          {hasProAccess
            ? "📡 Pro analytics active"
            : "📍 Map-powered campaigns"}
        </Text>
      </Box>
    </Box>
  );
}